// ─── INVENTARIO ──────────────────────────────────────────────────────────────
// Existencias que se cargan de un Excel (o del sistema Estrategia) o se capturan
// a mano; cada artículo se puede editar o eliminar. El backend
// (/api/inventario) guarda un documento por usuario {items, fecha_corte, fuente}
// y cada carga reemplaza todo. Metros (MT) y kilos (KG) nunca se suman entre sí.
// Cada tela puede traer colores [{color, existencia}] que se despliegan al darle clic.
import { useState, useEffect, useMemo, Fragment } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { api } from "../auth";
import { EMPRESA, cargarLogoPdf } from "../empresa";
import FotosArticulo, { urlFoto, claveFoto } from "./FotosArticulo";

const UNIDADES = [
  { id: "todas", label: "Todas" },
  { id: "MT", label: "Metros (MT)" },
  { id: "KG", label: "Kilos (KG)" },
];

const ORDENES = [
  { id: "alfabetico", label: "Descripción (A-Z)" },
  { id: "existencia", label: "Existencia (mayor a menor)" },
];

// Orden alfabético en español (ñ, acentos) y con números en orden natural ("Tela 2" antes de "Tela 10").
const alfabetico = (a, b) =>
  String(a.descripcion || "").localeCompare(String(b.descripcion || ""), "es", { sensitivity: "base", numeric: true }) ||
  String(a.id || "").localeCompare(String(b.id || ""), "es", { numeric: true });

const cantidad = (n) =>
  isFinite(Number(n))
    ? Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

// "2026-09-22" → "22/09/2026"; cualquier otro formato se muestra tal cual.
const fecha = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ""));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s || "—";
};

// Quita acentos y pasa a minúsculas para que la búsqueda no dependa de ellos.
const normalizar = (s) =>
  String(s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const claveItem = (it, i) => it.uid || `${it.id || ""}|${it.descripcion || ""}|${i}`;

// Colores con existencia distinta de 0, de mayor a menor (los negativos al final, en rojo).
// Colores que no se consideran disponibles: negativos y saldos chicos
// (menos de 40 MT o 10 KG). Se quitan del desglose y la existencia de la tela
// pasa a ser la suma de los colores que quedan. Es una regla de la app: los
// datos guardados no cambian, así que aplica igual tras cada carga de Estrategia.
const MINIMO_COLOR = { MT: 40, KG: 10 };
const aplicarMinimos = (it) => {
  const colores = Array.isArray(it.colores) ? it.colores : [];
  if (colores.length === 0) return it; // sin desglose por color: se respeta la existencia original
  const minimo = MINIMO_COLOR[String(it.unidad || "").toUpperCase()] || 0;
  const quedan = colores.filter((c) => Number(c.existencia) > 0 && Number(c.existencia) >= minimo);
  return {
    ...it,
    colores: quedan,
    existencia: quedan.reduce((s, c) => s + Number(c.existencia), 0),
    coloresQuitados: colores.length - quedan.length,
  };
};

const coloresVisibles = (it) =>
  (Array.isArray(it.colores) ? it.colores : [])
    .filter((c) => Number(c.existencia) !== 0 && isFinite(Number(c.existencia)))
    .sort((a, b) => Number(b.existencia) - Number(a.existencia));

// Llave igual que el servidor de pedidos: mayúsculas y espacios normalizados.
const llaveAp = (x) => String(x || "").toUpperCase().replace(/\s+/g, " ").trim();

function DesgloseColores({ it, q, apartado = {} }) {
  const colores = coloresVisibles(it);
  if (colores.length === 0) {
    return <p className="text-xs text-slate-500 px-3 py-2">Sin desglose por color.</p>;
  }
  const total = colores.reduce((s, c) => s + Number(c.existencia), 0);
  const diferencia = total - (Number(it.existencia) || 0);
  const u = it.unidad || "";
  const hayApartado = Object.keys(apartado).length > 0;
  const totalApartado = colores.reduce((s, c) => s + (apartado[llaveAp(c.color)] || 0), 0);
  return (
    <div className="px-3 py-2">
      <table className="text-xs min-w-[280px]" data-testid={`inventario-colores-${it.id}`}>
        <thead>
          <tr className="text-slate-500">
            <th className="text-left pr-6 py-1 font-semibold">Color</th>
            <th className="text-right py-1 font-semibold">Existencia ({u || "—"})</th>
            {hayApartado && (
              <>
                <th className="text-right py-1 pl-4 font-semibold">Apartado</th>
                <th className="text-right py-1 pl-4 font-semibold">Disponible</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {colores.map((c, i) => {
            const coincide = q && normalizar(c.color).includes(q);
            return (
              <tr key={`${c.color}-${i}`} className={`border-t border-slate-200/70 ${coincide ? "bg-amber-100/70" : ""}`}>
                <td className="pr-6 py-1">{c.color || "—"}</td>
                <td className={`py-1 text-right whitespace-nowrap ${Number(c.existencia) < 0 ? "text-rose-600 font-semibold" : ""}`}>
                  {cantidad(c.existencia)}
                </td>
                {hayApartado && (
                  <>
                    <td className="py-1 pl-4 text-right whitespace-nowrap text-amber-700">{apartado[llaveAp(c.color)] ? cantidad(apartado[llaveAp(c.color)]) : "—"}</td>
                    <td className="py-1 pl-4 text-right whitespace-nowrap font-semibold">{cantidad(Number(c.existencia) - (apartado[llaveAp(c.color)] || 0))}</td>
                  </>
                )}
              </tr>
            );
          })}
          <tr className="border-t border-slate-300 font-semibold">
            <td className="pr-6 py-1">
              Total {colores.length} {colores.length === 1 ? "color" : "colores"}
            </td>
            <td className="py-1 text-right whitespace-nowrap">
              {cantidad(total)} {u}
            </td>
            {hayApartado && (
              <>
                <td className="py-1 pl-4 text-right whitespace-nowrap text-amber-700">{cantidad(totalApartado)}</td>
                <td className="py-1 pl-4 text-right whitespace-nowrap">{cantidad(total - totalApartado)}</td>
              </>
            )}
          </tr>
        </tbody>
      </table>
      {Math.abs(diferencia) >= 0.005 && (
        <p className="text-[11px] text-slate-400 mt-1" data-testid="inventario-colores-descuadre">
          No cuadra con la existencia global ({cantidad(it.existencia)} {u}); diferencia {diferencia > 0 ? "+" : ""}
          {cantidad(diferencia)}.
        </p>
      )}
    </div>
  );
}

const inputCls =
  "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500";
const labelCls = "block text-xs font-semibold text-slate-600 mb-1";

// Formulario para dar de alta, editar o eliminar un artículo (incluye su desglose por color).
// Sin item.uid es un artículo nuevo.
function EditarArticulo({ item, onGuardado, onEliminado, onCerrar }) {
  const nuevo = !item.uid;
  const [f, setF] = useState(() => ({
    id: item.id || "",
    descripcion: item.descripcion || "",
    existencia: String(item.existencia ?? ""),
    unidad: item.unidad || "MT",
    ancho: item.ancho ?? "",
    peso: item.peso ?? "",
    status: item.status || "",
    colores: (item.colores || []).map((c) => ({ color: c.color || "", existencia: String(c.existencia ?? "") })),
  }));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const set = (campo) => (e) => setF((x) => ({ ...x, [campo]: e.target.value }));
  const setColor = (i, campo) => (e) =>
    setF((x) => ({ ...x, colores: x.colores.map((c, j) => (j === i ? { ...c, [campo]: e.target.value } : c)) }));
  const numero = (v) => Number(String(v).replace(/[,\s]/g, ""));

  const sumaColores = f.colores.reduce((s, c) => s + (isFinite(numero(c.existencia)) ? numero(c.existencia) : 0), 0);
  const conColores = f.colores.some((c) => c.color.trim() || String(c.existencia).trim());
  const descuadre = conColores && Math.abs(sumaColores - numero(f.existencia)) >= 0.005;

  const guardar = async (e) => {
    e.preventDefault();
    if (!f.id.trim() && !f.descripcion.trim()) return setError("Pon al menos la clave o la descripción.");
    const colores = f.colores
      .filter((c) => c.color.trim() || String(c.existencia).trim())
      .map((c) => ({ color: c.color.trim(), existencia: numero(c.existencia) }));
    if (colores.some((c) => !c.color || !isFinite(c.existencia)))
      return setError("Cada color necesita nombre y una existencia numérica.");
    // Sin existencia capturada pero con colores: la existencia es la suma de los colores.
    const existencia = String(f.existencia).trim() === "" && colores.length ? sumaColores : numero(f.existencia);
    if (!isFinite(existencia)) return setError("La existencia debe ser un número.");
    setGuardando(true);
    setError("");
    try {
      const ruta = nuevo ? "/inventario/items" : `/inventario/items/${encodeURIComponent(item.uid)}`;
      const saved = await api.post(ruta, {
        id: f.id.trim(),
        descripcion: f.descripcion.trim(),
        existencia,
        unidad: f.unidad,
        ancho: String(f.ancho).trim(),
        peso: String(f.peso).trim(),
        status: f.status.trim(),
        colores,
      });
      onGuardado(saved);
    } catch (err) {
      const msg = String(err.message);
      setError(
        msg.includes("409")
          ? "Ya existe un artículo con esa clave; búscalo en la lista y edítalo."
          : msg.includes("404")
          ? "Este artículo ya no existe; recarga la página."
          : "No se pudo guardar. Intenta de nuevo."
      );
      setGuardando(false);
    }
  };

  const eliminar = async () => {
    if (!window.confirm(`¿Eliminar "${item.descripcion || item.id}" del inventario?`)) return;
    setGuardando(true);
    try {
      await api.del(`/inventario/items/${encodeURIComponent(item.uid)}`);
      onEliminado(item.uid);
    } catch (err) {
      setError(String(err.message).includes("404") ? "Este artículo ya no existe; recarga la página." : "No se pudo eliminar. Intenta de nuevo.");
      setGuardando(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={() => !guardando && onCerrar()}
      data-testid="inventario-modal"
    >
      <form onSubmit={guardar} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl p-5 space-y-3 my-4">
        <p className="text-sm font-bold text-slate-800">{nuevo ? "Nuevo artículo" : "Editar artículo"}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>Clave</label>
            <input className={`${inputCls} font-mono`} value={f.id} onChange={set("id")} data-testid="inventario-clave" />
          </div>
          <div className="sm:col-span-3">
            <label className={labelCls}>Descripción</label>
            <input className={inputCls} value={f.descripcion} onChange={set("descripcion")} autoFocus data-testid="inventario-descripcion" />
          </div>
          <div>
            <label className={labelCls}>Existencia</label>
            <input
              inputMode="decimal"
              className={inputCls}
              value={f.existencia}
              onChange={set("existencia")}
              placeholder={conColores ? "Suma de colores" : "0.00"}
              data-testid="inventario-existencia"
            />
          </div>
          <div>
            <label className={labelCls}>Unidad</label>
            <select className={inputCls} value={f.unidad} onChange={set("unidad")}>
              <option value="MT">MT</option>
              <option value="KG">KG</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Ancho (m)</label>
            <input className={inputCls} value={f.ancho} onChange={set("ancho")} />
          </div>
          <div>
            <label className={labelCls}>Peso (kg/m² o gsm)</label>
            <input className={inputCls} value={f.peso} onChange={set("peso")} />
          </div>
          <div className="col-span-2 sm:col-span-4">
            <label className={labelCls}>Status</label>
            <input className={inputCls} value={f.status} onChange={set("status")} />
          </div>
        </div>

        <div className="border border-slate-200 rounded-xl p-3 bg-slate-50 space-y-2">
          <p className="text-xs font-semibold text-slate-600">Colores ({f.unidad})</p>
          {f.colores.map((c, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input className={inputCls} value={c.color} onChange={setColor(i, "color")} placeholder="Color" data-testid={`inventario-color-${i}`} />
              <input inputMode="decimal" className={`${inputCls} w-36 text-right`} value={c.existencia} onChange={setColor(i, "existencia")} placeholder="0.00" data-testid={`inventario-color-existencia-${i}`} />
              <button
                type="button"
                onClick={() => setF((x) => ({ ...x, colores: x.colores.filter((_, j) => j !== i) }))}
                className="text-rose-600 hover:bg-rose-50 rounded-lg px-2 py-1 text-sm"
                title="Quitar color"
              >
                ✕
              </button>
            </div>
          ))}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setF((x) => ({ ...x, colores: [...x.colores, { color: "", existencia: "" }] }))}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-white"
              data-testid="inventario-agregar-color"
            >
              + Color
            </button>
            {conColores && (
              <p className={`text-[11px] ${descuadre ? "text-amber-700" : "text-slate-500"}`}>
                Suma de colores: {cantidad(sumaColores)} {f.unidad}
                {descuadre ? " · no cuadra con la existencia" : ""}
              </p>
            )}
          </div>
        </div>

        <p className="text-[11px] text-slate-400">
          {nuevo
            ? "Si dejas la existencia vacía y capturas colores, se toma la suma de los colores."
            : "Si después cargas un Excel con «Reemplazar todo», estos cambios se pierden."}
        </p>
        {error && <p className="text-xs text-rose-600 font-medium">{error}</p>}

        <div className="flex items-center justify-between gap-2 pt-1">
          {nuevo ? (
            <span />
          ) : (
            <button type="button" onClick={eliminar} disabled={guardando} data-testid="inventario-eliminar" className="text-xs font-semibold px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-50">
              Eliminar artículo
            </button>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={onCerrar} disabled={guardando} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
              Cancelar
            </button>
            <button type="submit" disabled={guardando} data-testid="inventario-guardar" className="text-xs font-semibold px-4 py-2 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
              {guardando ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// ─── CARGAR DESDE EXCEL ──────────────────────────────────────────────────────
// Un renglón por tela y color: Clave, Descripción, Color, Existencia, Unidad,
// Ancho, Peso, Status. Los encabezados se buscan por nombre (no por posición) y
// los renglones de la misma clave (o descripción) se juntan en una sola tela.
const COLUMNAS_PLANTILLA = ["Clave", "Descripción", "Color", "Existencia", "Unidad", "Ancho (m)", "Peso (gsm)", "Status"];

function descargarPlantilla() {
  const ws = XLSX.utils.aoa_to_sheet([
    COLUMNAS_PLANTILLA,
    ["LIN-001", "LINO LAVADO", "ARENA", 120, "MT", 1.5, 180, ""],
    ["LIN-001", "LINO LAVADO", "BLANCO", 85.5, "MT", 1.5, 180, ""],
    ["JQ-2811", "JACQUARD", "NEGRO", 300, "KG", 1.45, 220, ""],
  ]);
  ws["!cols"] = [{ wch: 12 }, { wch: 30 }, { wch: 16 }, { wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 11 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Inventario");
  XLSX.writeFile(wb, `${EMPRESA.nombre} - Plantilla de inventario.xlsx`);
}

const unidadExcel = (v) => {
  const u = normalizar(v).trim();
  if (!u) return "MT";
  if (/^(kg|kgs|kilo|kilos|kilogramo)/.test(u)) return "KG";
  if (/^(m|mt|mts|metro|metros|ml)\b/.test(u) || u === "m") return "MT";
  return String(v).trim().toUpperCase();
};
const numeroExcel = (v) => {
  if (typeof v === "number") return v;
  const n = Number(String(v ?? "").replace(/[$,\s]/g, ""));
  return isFinite(n) ? n : NaN;
};

function leerLibroInventario(wb) {
  for (const nombre of wb.SheetNames) {
    const rows = XLSX.utils.sheet_to_json(wb.Sheets[nombre], { header: 1, defval: "" });
    const idx = rows.findIndex((r) => {
      const h = r.map((c) => normalizar(c).trim());
      return (h.includes("clave") || h.some((x) => /^(descripcion|tela|articulo)/.test(x))) && h.some((x) => /^(existencia|cantidad|stock)/.test(x));
    });
    if (idx === -1) continue;
    const enc = rows[idx].map((c) => normalizar(c).trim());
    const col = (re) => enc.findIndex((h) => re.test(h));
    const C = {
      clave: col(/^(clave|codigo|sku)/),
      desc: col(/^(descripcion|tela|articulo|nombre)/),
      color: col(/^color/),
      ex: col(/^(existencia|cantidad|stock)/),
      unidad: col(/^(unidad|um|u\.m)/),
      ancho: col(/^ancho/),
      peso: col(/^(peso|gramaje|gsm)/),
      status: col(/^(status|estatus|estado)/),
    };
    const txt = (r, i) => (i === -1 ? "" : String(r[i] ?? "").trim());
    const porTela = new Map();
    let renglones = 0;
    let errores = 0;
    for (const r of rows.slice(idx + 1)) {
      const clave = txt(r, C.clave);
      const descripcion = txt(r, C.desc);
      if (!clave && !descripcion) continue;
      const ex = numeroExcel(C.ex === -1 ? "" : r[C.ex]);
      if (!isFinite(ex)) {
        errores++;
        continue;
      }
      renglones++;
      const llave = normalizar(clave || descripcion).trim();
      let it = porTela.get(llave);
      if (!it) {
        it = { id: clave, descripcion, existencia: 0, unidad: unidadExcel(txt(r, C.unidad)), ancho: txt(r, C.ancho), peso: txt(r, C.peso), status: txt(r, C.status), colores: [] };
        porTela.set(llave, it);
      }
      if (!it.descripcion && descripcion) it.descripcion = descripcion;
      const color = txt(r, C.color);
      if (color) {
        const previo = it.colores.find((c) => normalizar(c.color).trim() === normalizar(color).trim());
        if (previo) previo.existencia += ex;
        else it.colores.push({ color, existencia: ex });
      }
      it.existencia += ex;
    }
    return { hoja: nombre, items: [...porTela.values()], renglones, errores };
  }
  return null;
}

// Pregunta si la carga reemplaza todo o solo agrega/actualiza por clave.
function ConfirmarCarga({ carga, actuales, onAplicar, onCerrar }) {
  const [guardando, setGuardando] = useState(false);
  const llave = (it) => normalizar(it.id || it.descripcion).trim();
  const existentes = new Set(actuales.map(llave));
  const actualiza = carga.items.filter((it) => existentes.has(llave(it))).length;
  const colores = carga.items.reduce((s, it) => s + it.colores.length, 0);
  const aplicar = async (modo) => {
    setGuardando(true);
    const nuevos = new Map(carga.items.map((it) => [llave(it), it]));
    const items =
      modo === "reemplazar"
        ? carga.items
        : [...actuales.filter((it) => !nuevos.has(llave(it))), ...carga.items];
    await onAplicar(items, modo);
    setGuardando(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start sm:items-center justify-center p-3" data-testid="inventario-carga">
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 my-4">
        <p className="text-sm font-bold text-slate-800">Cargar inventario de Excel</p>
        <p className="text-sm text-slate-600" data-testid="inventario-carga-resumen">
          «{carga.archivo}» (hoja {carga.hoja}): <b>{carga.items.length}</b> {carga.items.length === 1 ? "tela" : "telas"}
          {colores ? `, ${colores} colores` : ""} de {carga.renglones} renglones.
          {carga.errores ? ` Se saltaron ${carga.errores} renglones sin existencia numérica.` : ""}
        </p>
        <div className="space-y-2">
          <button
            type="button"
            disabled={guardando}
            onClick={() => aplicar("agregar")}
            data-testid="inventario-carga-agregar"
            className="w-full text-left rounded-xl border-2 border-teal-500 bg-teal-50 px-3 py-2 hover:bg-teal-100 disabled:opacity-50"
          >
            <p className="text-sm font-semibold text-slate-800">Agregar y actualizar</p>
            <p className="text-[11px] text-slate-500">
              {carga.items.length - actualiza} nuevas · {actualiza} ya existían y se reemplazan · las demás telas se quedan igual
            </p>
          </button>
          <button
            type="button"
            disabled={guardando}
            onClick={() => aplicar("reemplazar")}
            data-testid="inventario-carga-reemplazar"
            className="w-full text-left rounded-xl border-2 border-slate-200 px-3 py-2 hover:border-rose-300 hover:bg-rose-50 disabled:opacity-50"
          >
            <p className="text-sm font-semibold text-slate-800">Reemplazar todo el inventario</p>
            <p className="text-[11px] text-slate-500">
              Borra las {actuales.length} telas actuales y deja solo las del archivo (las fotos se conservan por clave).
            </p>
          </button>
        </div>
        <div className="flex justify-end">
          <button type="button" onClick={onCerrar} disabled={guardando} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EXPORTAR (Excel / PDF) ──────────────────────────────────────────────────
// Exporta lo que se ve en pantalla (búsqueda, filtro de unidad y orden aplicados).
const hoyArchivo = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()}`;
};
const valorOpcional = (v) => (v === "" || v == null ? "" : v);

// Ancho en metros con 2 decimales (1.50). Si viene en cm (150) se pasa a metros.
const anchoMetros = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  if (v === "" || v == null || !isFinite(n) || n <= 0) return null;
  return n > 10 ? n / 100 : n;
};
const fmtAncho = (v) => {
  const m = anchoMetros(v);
  return m === null ? (v === "" || v == null ? "—" : String(v)) : m.toFixed(2);
};
// Peso en gramos por m² (gsm). Si viene en kg/m² (0.12) se pasa a gramos (120).
const pesoGsm = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  if (v === "" || v == null || !isFinite(n) || n <= 0) return null;
  return Math.round(n < 5 ? n * 1000 : n);
};
const fmtPeso = (v) => {
  const g = pesoGsm(v);
  return g === null ? (v === "" || v == null ? "—" : String(v)) : `${g} gsm`;
};

function totalesPorUnidad(items) {
  const t = {};
  for (const it of items) {
    const u = String(it.unidad || "").toUpperCase() || "Sin unidad";
    if (!t[u]) t[u] = { existencia: 0, articulos: 0 };
    t[u].existencia += Number(it.existencia) || 0;
    t[u].articulos += 1;
  }
  return Object.entries(t).sort(([a], [b]) => a.localeCompare(b));
}

function exportarExcel(items, datos) {
  const filas = items.map((it) => ({
    Clave: it.id || "",
    Descripción: it.descripcion || "",
    Existencia: Number(it.existencia) || 0,
    Unidad: it.unidad || "",
    "Ancho (m)": anchoMetros(it.ancho) ?? valorOpcional(it.ancho),
    "Peso (gsm)": pesoGsm(it.peso) ?? valorOpcional(it.peso),
    Status: it.status || "",
    Colores: coloresVisibles(it).length,
  }));
  const hoja = XLSX.utils.json_to_sheet(filas);
  // Ancho siempre con 2 decimales en Excel (1.50, no 1.5).
  filas.forEach((f, i) => {
    const celda = hoja[XLSX.utils.encode_cell({ r: i + 1, c: 4 })];
    if (celda && typeof celda.v === "number") celda.z = "0.00";
  });
  // Totales por unidad al final (MT y KG nunca se suman entre sí).
  const totales = totalesPorUnidad(items).map(([u, t]) => ["", `Total ${u} (${t.articulos} artículos)`, t.existencia, u]);
  XLSX.utils.sheet_add_aoa(hoja, [[], ...totales], { origin: -1 });
  hoja["!cols"] = [{ wch: 14 }, { wch: 40 }, { wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 9 }];

  const filasColores = [];
  for (const it of items) {
    for (const c of coloresVisibles(it)) {
      filasColores.push({
        Clave: it.id || "",
        Descripción: it.descripcion || "",
        Color: c.color || "",
        Existencia: Number(c.existencia) || 0,
        Unidad: it.unidad || "",
      });
    }
  }
  const hojaColores = XLSX.utils.json_to_sheet(
    filasColores.length ? filasColores : [{ Clave: "", Descripción: "Sin desglose por color", Color: "", Existencia: "", Unidad: "" }]
  );
  hojaColores["!cols"] = [{ wch: 14 }, { wch: 40 }, { wch: 24 }, { wch: 12 }, { wch: 8 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, hoja, "Inventario");
  XLSX.utils.book_append_sheet(wb, hojaColores, "Colores");
  const corte = datos.fecha_corte ? fecha(datos.fecha_corte).replace(/\//g, "-") : hoyArchivo();
  XLSX.writeFile(wb, `${EMPRESA.nombre} - Inventario ${corte}.xlsx`);
}

// Miniaturas como dataURL para incrustarlas en el PDF (solo la primera foto de cada tela).
async function miniaturasPDF(items, fotosPorClave) {
  const res = {};
  await Promise.all(
    items.map(async (it) => {
      const f = (fotosPorClave[claveFoto(it)] || [])[0];
      if (!f) return;
      try {
        const blob = await fetch(urlFoto(f.mini_id || f.id), { credentials: "include" }).then((r) => (r.ok ? r.blob() : null));
        if (!blob) return;
        res[claveFoto(it)] = await new Promise((ok) => {
          const fr = new FileReader();
          fr.onload = () => ok(fr.result);
          fr.readAsDataURL(blob);
        });
      } catch (e) {
        // sin foto en el PDF
      }
    })
  );
  return res;
}

async function exportarPDF(items, datos, filtroTexto, fotosPorClave = null) {
  const minis = fotosPorClave ? await miniaturasPDF(items, fotosPorClave) : null;
  const doc = new jsPDF({ unit: "pt", format: "letter", orientation: "landscape" });
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(0, 0, 0);
  doc.rect(0, 0, W, 80, "F");
  try {
    const base64 = await cargarLogoPdf();
    doc.addImage(base64, "JPEG", W / 2 - 82, 8, 165, 60, undefined, "FAST");
  } catch (e) {
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(EMPRESA.razonSocial, W / 2, 48, { align: "center" });
  }

  let y = 108;
  doc.setTextColor(15, 118, 110);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text("INVENTARIO", 40, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(71, 85, 105);
  const sub = [
    datos.fecha_corte ? `Existencias al ${fecha(datos.fecha_corte)}` : "Sin fecha de corte",
    `${items.length} ${items.length === 1 ? "artículo" : "artículos"}`,
    filtroTexto,
  ].filter(Boolean);
  doc.text(sub.join(" · "), 40, y + 16);
  const tot = totalesPorUnidad(items).map(([u, t]) => `Total ${u}: ${cantidad(t.existencia)} ${u}`).join("     ");
  doc.setFont("helvetica", "bold");
  doc.setTextColor(30, 41, 59);
  doc.text(tot, W - 40, y + 16, { align: "right" });

  // Un renglón por tela y, debajo, sus colores (sin los que están en 0).
  const body = [];
  const conFoto = !!minis;
  const off = conFoto ? 1 : 0; // la columna Foto recorre los índices
  for (const it of items) {
    body.push({
      tipo: "tela",
      foto: minis ? minis[claveFoto(it)] : null,
      celdas: [...(conFoto ? [""] : []), it.id || "—", it.descripcion || "—", cantidad(it.existencia), it.unidad || "—",
        fmtAncho(it.ancho), fmtPeso(it.peso), it.status || "—"],
    });
    for (const c of coloresVisibles(it)) {
      body.push({
        tipo: "color",
        negativo: Number(c.existencia) < 0,
        celdas: [...(conFoto ? [""] : []), "", `      ${c.color || "—"}`, cantidad(c.existencia), it.unidad || "", "", "", ""],
      });
    }
  }

  autoTable(doc, {
    startY: y + 30,
    head: [[...(conFoto ? ["Foto"] : []), "Clave", "Descripción / color", "Existencia", "Unidad", "Ancho", "Peso", "Status"]],
    body: body.map((r) => r.celdas),
    theme: "grid",
    styles: { fontSize: 8.5, cellPadding: 4, lineColor: [226, 232, 240], lineWidth: 0.5 },
    headStyles: { fillColor: [15, 118, 110], textColor: 255, fontStyle: "bold" },
    columnStyles: {
      ...(conFoto ? { 0: { cellWidth: 52 } } : {}),
      [0 + off]: { cellWidth: 80 },
      [2 + off]: { halign: "right", cellWidth: 75 },
      [3 + off]: { cellWidth: 45 },
      [4 + off]: { halign: "right", cellWidth: 50 },
      [5 + off]: { halign: "right", cellWidth: 60 },
      [6 + off]: { cellWidth: 80 },
    },
    margin: { left: 40, right: 40 },
    didParseCell: (data) => {
      if (data.section !== "body") return;
      const fila = body[data.row.index];
      if (fila.tipo === "tela") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [240, 253, 250];
        if (fila.foto) data.cell.styles.minCellHeight = 46;
      } else {
        data.cell.styles.textColor = fila.negativo && data.column.index === 2 + off ? [225, 29, 72] : [71, 85, 105];
        data.cell.styles.fontSize = 8;
      }
    },
    didDrawCell: (data) => {
      if (!conFoto || data.section !== "body" || data.column.index !== 0) return;
      const fila = body[data.row.index];
      if (fila.tipo !== "tela" || !fila.foto) return;
      try {
        // Ajusta la foto a la celda sin deformarla.
        const { width, height, fileType } = doc.getImageProperties(fila.foto);
        const esc = Math.min((data.cell.width - 6) / width, (data.cell.height - 6) / height);
        const w = width * esc;
        const h = height * esc;
        doc.addImage(fila.foto, fileType || "JPEG", data.cell.x + (data.cell.width - w) / 2, data.cell.y + (data.cell.height - h) / 2, w, h, undefined, "FAST");
      } catch (e) {
        // imagen no válida: se deja la celda vacía
      }
    },
    didDrawPage: () => {
      const H = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(148, 163, 184);
      doc.text(`${EMPRESA.razonSocial} · Generado ${fecha(new Date().toISOString())} · Página ${doc.internal.getNumberOfPages()}`, W / 2, H - 20, { align: "center" });
    },
  });

  const corte = datos.fecha_corte ? fecha(datos.fecha_corte).replace(/\//g, "-") : hoyArchivo();
  doc.save(`${EMPRESA.nombre} - Inventario ${corte}.pdf`);
}

export default function Inventario() {
  const [datos, setDatos] = useState({ items: [], fecha_corte: null, fuente: "" });
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [unidad, setUnidad] = useState("todas");
  const [orden, setOrden] = useState("alfabetico");
  // Filas que el usuario abrió o cerró a mano (se invierten respecto al estado automático).
  const [toggles, setToggles] = useState(() => new Set());
  const [editando, setEditando] = useState(null);
  const [exportando, setExportando] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [fotosDe, setFotosDe] = useState(null); // artículo con la galería abierta
  const [pdfConFotos, setPdfConFotos] = useState(false);
  const [apartados, setApartados] = useState({}); // clave → color → cantidad apartada en pedidos abiertos
  const [carga, setCarga] = useState(null); // Excel leído, esperando confirmación
  const [cargaMsg, setCargaMsg] = useState("");

  const leerExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCargaMsg("");
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const r = leerLibroInventario(wb);
      if (!r || !r.items.length)
        return setCargaMsg("No encontré inventario en el archivo. Necesita columnas «Clave» o «Descripción» y «Existencia» (descarga la plantilla).");
      setCarga({ ...r, archivo: file.name });
    } catch (err) {
      console.error("Error leyendo Excel de inventario:", err);
      setCargaMsg("No se pudo leer el archivo. ¿Es un Excel (.xlsx o .xls)?");
    }
  };

  const aplicarCarga = async (items, modo) => {
    try {
      const saved = await api.post("/inventario", {
        items,
        fecha_corte: new Date().toISOString().slice(0, 10),
        fuente: `Excel (${carga.archivo})`,
      });
      if (saved && Array.isArray(saved.items)) setDatos(saved);
      setCargaMsg(
        `✅ Inventario ${modo === "reemplazar" ? "reemplazado" : "actualizado"} con «${carga.archivo}»: ${saved?.items?.length ?? items.length} telas en total.`
      );
      setCarga(null);
    } catch (err) {
      console.error("No se pudo guardar el inventario:", err);
      alert("No se pudo guardar el inventario. Intenta de nuevo.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await api.get("/inventario");
        if (data && Array.isArray(data.items)) setDatos(data);
      } catch (e) {
        // silencioso
      }
      setLoading(false);
    })();
    (async () => {
      try {
        const data = await api.get("/inventario/fotos");
        setFotos(Array.isArray(data?.fotos) ? data.fotos : []);
      } catch (e) {
        // sin fotos
      }
    })();
    (async () => {
      try {
        const data = await api.get("/pedidos/apartados");
        setApartados(data?.apartados || {});
      } catch (e) {
        // sin apartados
      }
    })();
  }, []);

  const fotosPorClave = useMemo(() => {
    const m = {};
    for (const f of fotos) (m[f.clave] = m[f.clave] || []).push(f);
    return m;
  }, [fotos]);

  // Inventario ya con la regla de mínimos por color aplicada (lo que se ve, suma y exporta).
  const items = useMemo(() => datos.items.map(aplicarMinimos), [datos.items]);
  const quitados = useMemo(() => items.reduce((s, it) => s + (it.coloresQuitados || 0), 0), [items]);

  const filtrados = useMemo(() => {
    const q = normalizar(busqueda.trim());
    return items
      .map((it, i) => ({ it, clave: claveItem(it, i) }))
      .filter(({ it }) => unidad === "todas" || String(it.unidad || "").toUpperCase() === unidad)
      .map((r) => {
        const porTela = !q || [r.it.id, r.it.descripcion, r.it.status].some((v) => normalizar(v).includes(q));
        // Si la tela solo coincide por color, se despliega sola para mostrar el color.
        const porColor = !!q && !porTela && coloresVisibles(r.it).some((c) => normalizar(c.color).includes(q));
        return { ...r, visible: porTela || porColor, auto: porColor };
      })
      .filter((r) => r.visible)
      .sort((a, b) =>
        orden === "existencia"
          ? (Number(b.it.existencia) || 0) - (Number(a.it.existencia) || 0) || alfabetico(a.it, b.it)
          : alfabetico(a.it, b.it)
      );
  }, [items, busqueda, unidad, orden]);

  const q = normalizar(busqueda.trim());
  const alternar = (clave) =>
    setToggles((prev) => {
      const nuevo = new Set(prev);
      nuevo.has(clave) ? nuevo.delete(clave) : nuevo.add(clave);
      return nuevo;
    });

  // Totales por unidad: cada unidad se suma por separado.
  const totales = useMemo(() => {
    const t = {};
    for (const { it } of filtrados) {
      const u = String(it.unidad || "").toUpperCase() || "Sin unidad";
      if (!t[u]) t[u] = { existencia: 0, articulos: 0 };
      t[u].existencia += Number(it.existencia) || 0;
      t[u].articulos += 1;
    }
    return Object.entries(t).sort(([a], [b]) => a.localeCompare(b));
  }, [filtrados]);

  return (
    <div className="space-y-3 pb-8">
      {fotosDe && (
        <FotosArticulo
          item={fotosDe}
          fotos={fotosPorClave[claveFoto(fotosDe)] || []}
          colores={(fotosDe.colores || []).map((c) => c.color).filter(Boolean)}
          onCambio={(nuevas) => {
            const clave = claveFoto(fotosDe);
            setFotos((todas) => [...todas.filter((f) => f.clave !== clave), ...nuevas]);
          }}
          onCerrar={() => setFotosDe(null)}
        />
      )}
      {carga && <ConfirmarCarga carga={carga} actuales={datos.items} onAplicar={aplicarCarga} onCerrar={() => setCarga(null)} />}
      {editando && (
        <EditarArticulo
          item={editando}
          onCerrar={() => setEditando(null)}
          onGuardado={(saved) => {
            setDatos((d) =>
              editando.uid
                ? { ...d, items: d.items.map((x) => (x.uid === saved.uid ? saved : x)) }
                : { ...d, items: [...d.items, saved], fuente: d.fuente || "Captura manual", fecha_corte: d.fecha_corte || new Date().toISOString().slice(0, 10) }
            );
            setEditando(null);
          }}
          onEliminado={(uid) => {
            setDatos((d) => ({ ...d, items: d.items.filter((x) => x.uid !== uid) }));
            setEditando(null);
          }}
        />
      )}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-semibold text-teal-700 uppercase tracking-wider">Inventario</p>
          <p className="text-sm text-slate-600 mt-0.5" data-testid="inventario-corte">
            {datos.fecha_corte ? `Existencias al ${fecha(datos.fecha_corte)}` : "Sin fecha de corte"}
            {datos.fuente ? ` · Fuente: ${datos.fuente}` : ""}
            {` · ${datos.items.length} ${datos.items.length === 1 ? "artículo" : "artículos"}`}
          </p>
          {cargaMsg && (
            <p className="text-xs text-slate-600 mt-1" data-testid="inventario-carga-msg">
              {cargaMsg}
            </p>
          )}
          {quitados > 0 && (
            <p className="text-[11px] text-slate-400 mt-0.5" data-testid="inventario-minimos">
              No se muestran {quitados} {quitados === 1 ? "color" : "colores"} negativos o con menos de 40 MT / 10 KG; la
              existencia de cada tela es la suma de sus colores disponibles.
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button
            type="button"
            onClick={() => setEditando({ unidad: "MT", colores: [{ color: "", existencia: "" }] })}
            data-testid="inventario-nuevo"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-teal-600 text-white hover:bg-teal-700"
          >
            + Artículo
          </button>
          <label
            data-testid="inventario-cargar-excel"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-white border-2 border-dashed border-teal-400 text-teal-700 hover:bg-teal-50 cursor-pointer"
            title="Un renglón por tela y color: Clave, Descripción, Color, Existencia, Unidad…"
          >
            📁 Cargar Excel
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={leerExcel} data-testid="inventario-excel-input" />
          </label>
          <button
            type="button"
            onClick={descargarPlantilla}
            data-testid="inventario-plantilla"
            className="text-[11px] font-medium text-teal-700 hover:underline whitespace-nowrap"
          >
            Plantilla
          </button>
          <span className="w-px h-6 bg-slate-200 mx-1" />
          <button
            type="button"
            disabled={!filtrados.length || exportando}
            onClick={() => exportarExcel(filtrados.map((r) => r.it), datos)}
            data-testid="inventario-exportar-excel"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            title="Descargar lo que se ve en pantalla en Excel"
          >
            ⬇ Excel
          </button>
          <button
            type="button"
            disabled={!filtrados.length || exportando}
            onClick={async () => {
              setExportando(true);
              try {
                const filtro = [
                  unidad !== "todas" ? `Unidad: ${unidad}` : "",
                  busqueda.trim() ? `Búsqueda: "${busqueda.trim()}"` : "",
                ].filter(Boolean).join(" · ");
                await exportarPDF(filtrados.map((r) => r.it), datos, filtro, pdfConFotos ? fotosPorClave : null);
              } catch (e) {
                console.error("No se pudo generar el PDF:", e);
                alert("No se pudo generar el PDF. Intenta de nuevo.");
              }
              setExportando(false);
            }}
            data-testid="inventario-exportar-pdf"
            className="text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"
            title="Descargar lo que se ve en pantalla en PDF"
          >
            {exportando ? "Generando…" : "⬇ PDF"}
          </button>
          <label className="text-[11px] text-slate-500 flex items-center gap-1 whitespace-nowrap" title="Incluye la foto de cada tela en el PDF (sirve como catálogo para clientes)">
            <input type="checkbox" checked={pdfConFotos} onChange={(e) => setPdfConFotos(e.target.checked)} data-testid="inventario-pdf-fotos" />
            PDF con fotos
          </label>
        </div>
        <div className="flex gap-6 flex-wrap">
          {totales.map(([u, t]) => (
            <div key={u} className="text-right" data-testid={`inventario-total-${u}`}>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                Total {u}{busqueda.trim() || unidad !== "todas" ? " (filtro)" : ""}
              </p>
              <p className="text-lg font-bold text-slate-800">
                {cantidad(t.existencia)} <span className="text-sm font-semibold text-slate-500">{u}</span>
              </p>
              <p className="text-[11px] text-slate-500">
                {t.articulos} {t.articulos === 1 ? "artículo" : "artículos"}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-3 flex gap-2 flex-wrap sm:flex-nowrap">
        <input
          type="text"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="🔎 Buscar: clave, descripción, color, status…"
          data-testid="inventario-buscar"
          className={inputCls}
        />
        <select
          value={unidad}
          onChange={(e) => setUnidad(e.target.value)}
          data-testid="inventario-unidad"
          className={`${inputCls} sm:w-48`}
        >
          {UNIDADES.map((u) => (
            <option key={u.id} value={u.id}>{u.label}</option>
          ))}
        </select>
        <select
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          data-testid="inventario-orden"
          className={`${inputCls} sm:w-64`}
        >
          {ORDENES.map((o) => (
            <option key={o.id} value={o.id}>Ordenar: {o.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                <th className="px-2 py-2 font-semibold w-14">Foto</th>
                <th className="text-left px-3 py-2 font-semibold">Clave</th>
                <th className="text-left px-3 py-2 font-semibold">Descripción</th>
                <th className="text-right px-3 py-2 font-semibold">Existencia</th>
                <th className="text-left px-3 py-2 font-semibold">Unidad</th>
                <th className="text-right px-3 py-2 font-semibold">Ancho</th>
                <th className="text-right px-3 py-2 font-semibold">Peso</th>
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtrados.map(({ it, clave, auto }) => {
                const abierta = auto !== toggles.has(clave);
                const nColores = coloresVisibles(it).length;
                return (
                <Fragment key={clave}>
                <tr
                  onClick={() => alternar(clave)}
                  className={`border-t border-slate-100 cursor-pointer ${abierta ? "bg-teal-50/60" : "hover:bg-teal-50/40"}`}
                  title={nColores ? "Clic para ver colores" : "Sin desglose por color"}
                  data-testid={`inventario-row-${it.id}`}
                >
                  <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    {(() => {
                      const fs = fotosPorClave[claveFoto(it)] || [];
                      return (
                        <button
                          type="button"
                          onClick={() => setFotosDe(it)}
                          data-testid={`inventario-foto-${it.id}`}
                          className="relative w-11 h-11 rounded-lg overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center hover:border-teal-400"
                          title={fs.length ? `${fs.length} ${fs.length === 1 ? "foto" : "fotos"}` : "Agregar fotos"}
                        >
                          {fs.length ? (
                            <>
                              <img src={urlFoto(fs[0].mini_id || fs[0].id)} alt="" loading="lazy" className="w-full h-full object-cover" />
                              {fs.length > 1 && (
                                <span className="absolute bottom-0 right-0 text-[9px] font-bold bg-black/60 text-white px-1 rounded-tl">{fs.length}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-slate-300 text-lg">📷</span>
                          )}
                        </button>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 font-mono whitespace-nowrap">
                    <span className="inline-block w-3 text-slate-400">{nColores ? (abierta ? "▾" : "▸") : ""}</span>
                    {it.id || "—"}
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-800">
                    {it.descripcion || "—"}
                    {nColores > 0 && <span className="ml-2 text-[11px] text-slate-400">{nColores} {nColores === 1 ? "color" : "colores"}</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap font-semibold">
                    {cantidad(it.existencia)}
                    {(() => {
                      const ap = Object.values(apartados[llaveAp(it.id)] || {}).reduce((t, v) => t + v, 0);
                      return ap > 0 ? (
                        <div className="text-[10px] font-normal text-amber-700" title="Apartado en pedidos abiertos">
                          apartado {cantidad(ap)} · disp. {cantidad(Number(it.existencia) - ap)}
                        </div>
                      ) : null;
                    })()}
                  </td>
                  <td className="px-3 py-2">{it.unidad || "—"}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtAncho(it.ancho)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{fmtPeso(it.peso)}</td>
                  <td className="px-3 py-2">
                    {it.status || "—"}
                    {it.editado && (
                      <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title="Editado a mano en la app">
                        editado
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {it.uid && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditando(datos.items.find((x) => x.uid === it.uid) || it);
                        }}
                        data-testid={`inventario-editar-${it.id}`}
                        className="text-xs font-semibold px-2 py-1 rounded-lg text-slate-500 hover:text-teal-700 hover:bg-teal-50"
                        title="Editar o eliminar"
                      >
                        ✏️ Editar
                      </button>
                    )}
                  </td>
                </tr>
                {abierta && (
                  <tr className="bg-slate-50">
                    <td colSpan={9} className="pl-8">
                      <DesgloseColores it={it} q={q} apartado={apartados[llaveAp(it.id)] || {}} />
                    </td>
                  </tr>
                )}
                </Fragment>
                );
              })}
              {!loading && filtrados.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-slate-400 py-6">
                    {datos.items.length === 0
                      ? "Aún no hay inventario. Carga un Excel o da de alta el primer artículo con «+ Artículo»."
                      : "Ningún artículo coincide con la búsqueda."}
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={9} className="text-center text-slate-400 py-6">Cargando…</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
